import { API_ENDPOINTS } from '@/config/api.config';
import apiClient from './api';

// ==========================================
// TYPE DEFINITIONS
// ==========================================

/**
 * Learning Path Assignment Settings
 */
export interface LearningPathAssignmentSettings {
  is_mandatory: boolean;
  send_notification: boolean;
  enable_automatic_reminder: boolean;
  reminder_frequency_days: number;
  deadline_date?: string;
  message?: string;
}

/**
 * Request to assign learning paths to users
 */
export interface AssignLearningPathToUsersRequest {
  user_ids: string[];
  learning_path_ids: string[];
  is_mandatory?: boolean;
  deadline_date?: string;
  assigned_date?: string;
  send_notification?: boolean;
  enable_automatic_reminder?: boolean;
  reminder_frequency_days?: number;
  message?: string;
}

/**
 * Request to assign learning paths to groups
 */
export interface AssignLearningPathToGroupsRequest {
  group_keys: string[];
  learning_path_ids: string[];
  is_mandatory?: boolean;
  deadline_date?: string;
  assigned_date?: string;
  send_notification?: boolean;
  enable_automatic_reminder?: boolean;
  reminder_frequency_days?: number;
  message?: string;
}

/**
 * Request to assign learning paths to roles
 */
export interface AssignLearningPathToRolesRequest {
  role_keys: string[];
  learning_path_ids: string[];
  is_mandatory?: boolean;
  deadline_date?: string;
  assigned_date?: string;
  send_notification?: boolean;
  enable_automatic_reminder?: boolean;
  reminder_frequency_days?: number;
  message?: string;
}

/**
 * Learning Path Assignment Response (BulkLearningPathAssignmentResultDto from backend)
 */
export interface LearningPathAssignmentResponse {
  success?: boolean;
  message?: string;
  assignment_id?: string;
  created_at?: string;
  total_users_assigned?: number;
  learning_paths_assigned?: number;
  successful_assignments?: number;
  failed_assignments?: number;
  successful_assignments_count?: number;
  failed_assignments_count?: number;
  total_assignments?: number;
  total_assignments_count?: number;
  stats?: {
    total_learning_paths?: number;
    total_assignees?: number;
    total_courses_in_paths?: number;
    notifications_sent?: number;
    notifications_failed?: number;
  };
  duplicates?: {
    skipped_count: number;
    details: Array<{
      user_id?: string;
      group_key?: string;
      role_key?: string;
      learning_path_id: string;
      reason: string;
    }>;
  };
  skipped_users?: Array<{ user_id: string; user_email: string; user_name: string; learning_path_id: string; reason: string }>;
  skipped_groups?: Array<{ group_key: string; group_name: string; learning_path_id: string; reason: string }>;
  skipped_roles?: Array<{ group_key: string; role_key: string; role_name: string; learning_path_id: string; reason: string }>;
}

/**
 * Learning Path Assignment Entity
 */
export interface LearningPathAssignment {
  _id: string;
  assignment_type: 'USER' | 'GROUP' | 'ROLE';
  learning_path_ids: string[];
  learning_paths?: Array<{
    _id: string;
    title: string;
    description?: string;
    total_courses: number;
    thumbnail_image_url?: string;
  }>;
  user_ids?: string[];
  group_keys?: string[];
  role_keys?: string[];
  settings: LearningPathAssignmentSettings;
  status: 'ACTIVE' | 'COMPLETED' | 'CANCELLED' | 'EXPIRED';
  total_assignees: number;
  completed_count: number;
  in_progress_count: number;
  not_started_count: number;
  created_by: string;
  created_at: string;
  updated_at: string;
  expires_at?: string;
}

/**
 * Update Learning Path Assignment Request
 */
export interface UpdateLearningPathAssignmentRequest {
  settings?: Partial<LearningPathAssignmentSettings>;
  status?: 'ACTIVE' | 'COMPLETED' | 'CANCELLED' | 'EXPIRED';
  learning_path_ids?: string[];
}

/**
 * Learning Path Assignment List Filters
 */
export interface LearningPathAssignmentListFilters {
  page?: number;
  limit?: number;
  assignment_type?: 'USER' | 'GROUP' | 'ROLE';
  status?: 'ACTIVE' | 'COMPLETED' | 'CANCELLED' | 'EXPIRED';
  search?: string;
  learning_path_id?: string;
  created_by?: string;
}

/**
 * Recent Learning Path Assignment
 */
export interface RecentLearningPathAssignment {
  _id: string;
  assignment_type: 'USER' | 'GROUP' | 'ROLE';
  learning_path_count: number;
  learning_path_titles: string[];
  assignee_count: number;
  assignee_names?: string[];
  settings: LearningPathAssignmentSettings;
  status: string;
  created_by: string;
  created_at: string;
  total_assignees: number;
  completed_count: number;
  in_progress_count: number;
  not_started_count: number;
}

/**
 * Recent Learning Path Assignment Filters
 */
export interface RecentLearningPathAssignmentFilters {
  page?: number;
  limit?: number;
  status?: string;
  is_mandatory?: boolean;
  assignment_type?: 'USER' | 'GROUP' | 'ROLE';
}

/**
 * Notification Response for Learning Path Assignments (NotificationResultDto from backend)
 */
export interface LearningPathNotificationResponse {
  success: boolean;
  message?: string;
  data: {
    success_count: number;
    failure_count: number;
    notification_ids: string[];
  };
}

/**
 * Learning Path Assignment Statistics Response
 */
export interface LearningPathAssignmentStatsResponse {
  assignment_id: string;
  total_assignees: number;
  completed_count: number;
  in_progress_count: number;
  not_started_count: number;
  completion_rate: number;
  average_progress: number;
  updated_at: string;
}

/**
 * Duplicate Check Response for Learning Paths
 */
export interface LearningPathDuplicateCheckResponse {
  has_conflicts: boolean;
  conflicts: Array<{
    target_id: string;
    target_type: 'user' | 'group' | 'role';
    learning_path_id: string;
    conflict_type: 'duplicate' | 'active_assignment' | 'completed';
    existing_assignments?: any[];
    users?: string[]; // For group/role assignments
  }>;
  summary: {
    total_checked: number;
    total_conflicts: number;
    conflicts_by_type: Record<string, number>;
  };
}

/**
 * Bulk Import Request for Learning Path Assignments
 */
export interface BulkImportLearningPathAssignmentsRequest {
  csv_content: string;
  is_base_64?: boolean;
}

/**
 * Bulk Import Response
 */
export interface BulkImportLearningPathAssignmentsResponse {
  success: boolean;
  message: string;
  total_rows: number;
  successful_assignments: number;
  failed_assignments: number;
  errors?: Array<{
    row: number;
    field?: string;
    message: string;
  }>;
}

/**
 * Paginated API Response
 */
export interface PaginatedResponse<T> {
  status: 'success' | 'error';
  result: T[];
  total: number;
  page: number;
  limit: number;
  error?: {
    message: string;
    code?: string;
  };
}

/**
 * Generic API Response
 */
export interface APIResponse<T> {
  status: 'success' | 'error';
  success?: boolean;
  data?: T;
  result?: T;
  error?: {
    message: string;
    code?: string;
  };
}

// ==========================================
// LEARNING PATH ASSIGNMENT SERVICE
// ==========================================

class LearningPathAssignmentService {
  // ==========================================
  // ASSIGNMENT ENDPOINTS
  // ==========================================

  /**
   * Assign learning paths to users
   * POST /api/learning-path-assignments/assign/users
   */
  async assignToUsers(
    request: AssignLearningPathToUsersRequest
  ): Promise<LearningPathAssignmentResponse> {
    const response = await apiClient.post<{
      success: boolean;
      code: number;
      description: string;
      data: LearningPathAssignmentResponse;
    }>(API_ENDPOINTS.LEARNING_PATH_ASSIGNMENTS.ASSIGN_TO_USERS, request);

    if (!response.data.success) {
      throw new Error(
        response.data.description || 'Failed to assign learning paths to users'
      );
    }

    return response.data.data;
  }

  /**
   * Assign learning paths to groups
   * POST /api/learning-path-assignments/assign/groups
   */
  async assignToGroups(
    request: AssignLearningPathToGroupsRequest
  ): Promise<LearningPathAssignmentResponse> {
    const response = await apiClient.post<{
      success: boolean;
      code: number;
      description: string;
      data: LearningPathAssignmentResponse;
    }>(API_ENDPOINTS.LEARNING_PATH_ASSIGNMENTS.ASSIGN_TO_GROUPS, request);

    if (!response.data.success) {
      throw new Error(
        response.data.description || 'Failed to assign learning paths to groups'
      );
    }

    return response.data.data;
  }

  /**
   * Assign learning paths to roles
   * POST /api/learning-path-assignments/assign/roles
   */
  async assignToRoles(
    request: AssignLearningPathToRolesRequest
  ): Promise<LearningPathAssignmentResponse> {
    const response = await apiClient.post<{
      success: boolean;
      code: number;
      description: string;
      data: LearningPathAssignmentResponse;
    }>(API_ENDPOINTS.LEARNING_PATH_ASSIGNMENTS.ASSIGN_TO_ROLES, request);

    if (!response.data.success) {
      throw new Error(
        response.data.description || 'Failed to assign learning paths to roles'
      );
    }

    return response.data.data;
  }

  /**
   * List all learning path assignments with pagination and filters
   * POST /api/learning-path-assignments (sends filters in body)
   */
  async listAssignments(
    filters?: LearningPathAssignmentListFilters
  ): Promise<PaginatedResponse<LearningPathAssignment>> {
    const body = filters || {};

    const response = await apiClient.post<{
      success: boolean;
      code: number;
      description: string;
      data: {
        data: LearningPathAssignment[];
        total: number;
        page: string | number;
        limit: string | number;
        summary?: any;
      };
    }>(
      API_ENDPOINTS.LEARNING_PATH_ASSIGNMENTS.LIST,
      body
    );

    if (!response.data.success) {
      throw new Error(
        response.data.description || 'Failed to fetch learning path assignments'
      );
    }

    // Transform the API response to match PaginatedResponse structure
    return {
      status: 'success',
      result: response.data.data.data,
      total: response.data.data.total,
      page: typeof response.data.data.page === 'string' ? parseInt(response.data.data.page) : response.data.data.page,
      limit: typeof response.data.data.limit === 'string' ? parseInt(response.data.data.limit) : response.data.data.limit,
    };
  }

  /**
   * Get recent learning path assignments
   * GET /api/learning-path-assignments/recent
   */
  async getRecentAssignments(
    filters?: RecentLearningPathAssignmentFilters
  ): Promise<any> {
    try {
      const params: any = {};

      if (filters?.page) params.page = filters.page;
      if (filters?.limit) params.limit = filters.limit;
      if (filters?.status) params.status = filters.status;
      if (filters?.is_mandatory !== undefined) params.is_mandatory = filters.is_mandatory;
      if (filters?.assignment_type) params.assignment_type = filters.assignment_type;

      const response = await apiClient.get(API_ENDPOINTS.LEARNING_PATH_ASSIGNMENTS.RECENT, {
        params
      });

      return response.data.data;
    } catch (error: any) {
      console.error('Error getting recent learning path assignments:', error);
      throw new Error(
        error?.response?.data?.description || 'Failed to get recent learning path assignments'
      );
    }
  }

  /**
   * Get assignment by ID
   * GET /api/learning-path-assignments/{id}
   */
  async getAssignmentById(id: string): Promise<LearningPathAssignment> {
    const response = await apiClient.get<{
      success: boolean;
      code: number;
      description: string;
      data: LearningPathAssignment;
    }>(API_ENDPOINTS.LEARNING_PATH_ASSIGNMENTS.GET_BY_ID(id));

    if (!response.data.success) {
      throw new Error(
        response.data.description || 'Failed to fetch learning path assignment'
      );
    }

    return response.data.data;
  }

  /**
   * Update assignment
   * PUT /api/learning-path-assignments/{id}
   */
  async updateAssignment(
    id: string,
    request: UpdateLearningPathAssignmentRequest
  ): Promise<LearningPathAssignment> {
    const response = await apiClient.put<APIResponse<LearningPathAssignment>>(
      API_ENDPOINTS.LEARNING_PATH_ASSIGNMENTS.UPDATE(id),
      request
    );

    if (response.data.status === 'error') {
      throw new Error(
        response.data.error?.message || 'Failed to update learning path assignment'
      );
    }

    return response.data.result!;
  }

  /**
   * Delete/Cancel assignment
   * DELETE /api/learning-path-assignments/{id}
   */
  async deleteAssignment(
    id: string
  ): Promise<{ deleted: boolean; id: string; message: string }> {
    const response = await apiClient.delete<
      APIResponse<{ deleted: boolean; id: string; message: string }>
    >(API_ENDPOINTS.LEARNING_PATH_ASSIGNMENTS.DELETE(id));

    if (response.data.status === 'error') {
      throw new Error(
        response.data.error?.message || 'Failed to delete learning path assignment'
      );
    }

    return response.data.result!;
  }

  /**
   * Send reminder notifications for assignment
   * POST /api/learning-path-assignments/{id}/send-reminders
   */
  async sendReminderNotifications(id: string): Promise<{
    success_count: number;
    failure_count: number;
    notification_ids: string[];
    message?: string;
  }> {
    try {
      const response = await apiClient.post(
        API_ENDPOINTS.LEARNING_PATH_ASSIGNMENTS.SEND_REMINDERS(id)
      );

      // Return the data with message field included
      return response.data.data;
    } catch (error: any) {
      console.error('Error sending reminder notifications:', error);
      throw new Error(
        error?.response?.data?.description || 'Failed to send reminder notifications'
      );
    }
  }

  /**
   * Update assignment statistics
   * PUT /api/learning-path-assignments/{id}/stats
   */
  async updateAssignmentStats(
    id: string
  ): Promise<LearningPathAssignmentStatsResponse> {
    const response = await apiClient.put<APIResponse<LearningPathAssignmentStatsResponse>>(
      API_ENDPOINTS.LEARNING_PATH_ASSIGNMENTS.UPDATE_STATS(id)
    );

    if (response.data.status === 'error') {
      throw new Error(
        response.data.error?.message || 'Failed to update assignment statistics'
      );
    }

    return response.data.result!;
  }

  // ==========================================
  // DUPLICATE CHECKING ENDPOINTS
  // ==========================================

  /**
   * Check for duplicate assignments using the API endpoint
   * Handles individual users, groups, and roles
   */
  async checkForDuplicates(params: {
    type: 'users' | 'groups' | 'roles';
    targetIds: (string | { group_key: string; role_key: string })[];
    learningPathIds: string[];
  }): Promise<LearningPathDuplicateCheckResponse> {
    try {
      const response = await apiClient.post<{
        success: boolean;
        status: string;
        description?: string;
        data: {
          has_conflicts: boolean;
          conflicts: Array<{
            target_id: string;
            target_type: string;
            learning_path_id: string;
            conflict_type: string;
            existing_assignments?: any[];
            users?: string[];
          }>;
          summary: {
            total_checked: number;
            total_conflicts: number;
            conflicts_by_type: Record<string, number>;
          };
        };
      }>(API_ENDPOINTS.LEARNING_PATH_ASSIGNMENTS.CHECK_DUPLICATES, {
        type: params.type,
        target_ids: params.targetIds,
        learning_path_ids: params.learningPathIds,
      });

      if (response.data.success && response.data.data) {
        // Transform snake_case response to camelCase
        const result = response.data.data;
        return {
          has_conflicts: result.has_conflicts,
          conflicts: result.conflicts.map((conflict) => ({
            target_id: conflict.target_id,
            target_type: conflict.target_type as 'user' | 'group' | 'role',
            learning_path_id: conflict.learning_path_id,
            conflict_type: conflict.conflict_type as 'duplicate' | 'active_assignment' | 'completed',
            existing_assignments: conflict.existing_assignments,
            users: conflict.users,
          })),
          summary: {
            total_checked: result.summary.total_checked,
            total_conflicts: result.summary.total_conflicts,
            conflicts_by_type: result.summary.conflicts_by_type,
          },
        };
      }

      // Default response if API doesn't return expected format
      return {
        has_conflicts: false,
        conflicts: [],
        summary: {
          total_checked: 0,
          total_conflicts: 0,
          conflicts_by_type: {},
        },
      };
    } catch (error) {
      console.error('Failed to check for duplicates:', error);
      return {
        has_conflicts: false,
        conflicts: [],
        summary: {
          total_checked: 0,
          total_conflicts: 0,
          conflicts_by_type: {},
        },
      };
    }
  }

  // ==========================================
  // BULK IMPORT ENDPOINTS
  // ==========================================

  /**
   * Get CSV template for bulk import
   * GET /api/learning-path-assignments/bulk/template
   */
  async getBulkImportTemplate(): Promise<{
    template_url: string;
    headers: string[];
    sample_data: any[];
  }> {
    const response = await apiClient.get<{
      success: boolean;
      statusCode: number;
      description: string;
      data: {
        template_url: string;
        headers: string[];
        sample_data: any[];
      };
    }>(API_ENDPOINTS.LEARNING_PATH_ASSIGNMENTS.BULK_TEMPLATE);

    if (!response.data.success) {
      throw new Error(response.data.description || 'Failed to get CSV template');
    }

    return response.data.data;
  }

  /**
   * Import bulk learning path assignments from CSV
   * POST /api/learning-path-assignments/bulk/import
   */
  async importBulkAssignments(
    csvContent: string,
    isBase64: boolean = false
  ): Promise<BulkImportLearningPathAssignmentsResponse> {
    try {
      const response = await apiClient.post<{
        success: boolean;
        statusCode: number;
        description: string;
        data: BulkImportLearningPathAssignmentsResponse;
        error?: { error_description?: string };
      }>(API_ENDPOINTS.LEARNING_PATH_ASSIGNMENTS.BULK_IMPORT, {
        csv_content: csvContent,
        is_base_64: isBase64,
      });

      if (!response.data.success) {
        const detailedError =
          response.data.error?.error_description || response.data.description;
        throw new Error(detailedError || 'The import process failed.');
      }
      return response.data.data;
    } catch (error: any) {
      console.error('API Error in importBulkAssignments:', error.response?.data || error.message);
      const errorMessage =
        error?.response?.data?.error?.error_description ||
        error?.response?.data?.description ||
        error?.message ||
        'An unknown error occurred during the import.';

      throw new Error(errorMessage);
    }
  }

  /**
   * Validate CSV before import
   * POST /api/learning-path-assignments/validate
   */
  async validateBulkImport(params: {
    csv_content: string;
    is_base64?: boolean;
  }): Promise<{
    total_records: number;
    valid_count: number;
    invalid_count: number;
    validation_results: Array<{
      row_number: number;
      valid: boolean;
      errors: string[];
    }>;
  }> {
    const response = await apiClient.post<{
      success: boolean;
      code: number;
      description: string;
      data: {
        total_records: number;
        valid_count: number;
        invalid_count: number;
        validation_results: Array<{
          row_number: number;
          valid: boolean;
          errors: string[];
        }>;
      };
    }>(API_ENDPOINTS.LEARNING_PATH_ASSIGNMENTS.BULK_VALIDATE, {
      csv_content: params.csv_content,
      is_base64: params.is_base64 || false,
    });

    if (!response.data.success) {
      throw new Error(response.data.description || 'Failed to validate CSV');
    }

    return response.data.data;
  }

  /**
   * Import bulk learning path assignments from CSV
   * POST /api/learning-path-assignments/bulk-import
   */
  async bulkImport(params: {
    csv_content: string;
    is_base64?: boolean;
  }): Promise<{
    total_records: number;
    successful_records: number;
    failed_records: number;
    results?: Array<{
      row_number: number;
      status: 'success' | 'failed';
      error?: string;
    }>;
  }> {
    try {
      const response = await apiClient.post<{
        success: boolean;
        code: number;
        description: string;
        data: {
          total_records: number;
          successful_records: number;
          failed_records: number;
          results?: Array<{
            row_number: number;
            status: 'success' | 'failed';
            error?: string;
          }>;
        };
      }>(API_ENDPOINTS.LEARNING_PATH_ASSIGNMENTS.BULK_IMPORT, {
        csv_content: params.csv_content,
        is_base64: params.is_base64 || false,
      });

      if (!response.data.success) {
        const detailedError = response.data.description;
        throw new Error(detailedError || 'The import process failed.');
      }

      return response.data.data;
    } catch (error: any) {
      console.error('API Error in bulkImport:', error.response?.data || error.message);
      const errorMessage =
        error?.response?.data?.description ||
        error?.message ||
        'An unknown error occurred during the import.';

      throw new Error(errorMessage);
    }
  }

  // ==========================================
  // ANALYTICS METHODS
  // ==========================================

  /**
   * Get overview analytics for learning path assignments with granularity in body
   * POST /api/learning-path-assignments/overview
   */
  async getOverviewAnalytics(granularity: 'weekly' | 'monthly' | 'quarterly' | 'yearly' = 'monthly'): Promise<{
    totalAssigned: number;
    totalCompleted: number;
    completionRate: number;
    avgProgress: number;
    avgTimeSpent: number;
    avgScore: number;
    onTimeRate: number;
    engagementScore: number;
    periodComparison: {
      totalAssigned: {
        change: number;
      };
      completionRate: {
        change: number;
      };
    };
  }> {
    try {
      const response = await apiClient.post<{
        success: boolean;
        code: number;
        description: string;
        data: {
          totalAssigned: number;
          totalCompleted: number;
          completionRate: number;
          avgProgress: number;
          avgTimeSpent: number;
          avgScore: number;
          onTimeRate: number;
          engagementScore: number;
          periodComparison: {
            totalAssigned: {
              current_period: any;
              previous_period: any;
              changes: {
                assignments_change: number;
              };
            };
            completionRate: {
              current_period: any;
              previous_period: any;
              changes: {
                completion_change: number;
              };
            };
          };
        };
      }>(API_ENDPOINTS.LEARNING_PATH_ASSIGNMENTS.OVERVIEW, {
        granularity
      });

      if (!response.data.success) {
        throw new Error(response.data.description || 'Failed to get overview analytics');
      }

      const data = response.data.data;

      // Transform the response to match the expected format
      return {
        totalAssigned: data.totalAssigned,
        totalCompleted: data.totalCompleted,
        completionRate: data.completionRate,
        avgProgress: data.avgProgress,
        avgTimeSpent: data.avgTimeSpent,
        avgScore: data.avgScore,
        onTimeRate: data.onTimeRate,
        engagementScore: data.engagementScore,
        periodComparison: {
          totalAssigned: {
            change: data.periodComparison.totalAssigned.changes?.assignments_change || 0
          },
          completionRate: {
            change: data.periodComparison.completionRate.changes?.completion_change || 0
          }
        }
      };
    } catch (error: any) {
      console.error('Error getting overview analytics:', error);
      throw new Error(
        error?.response?.data?.description || 'Failed to get overview analytics'
      );
    }
  }

  // ==========================================
  // UTILITY METHODS
  // ==========================================

  /**
   * Get all assignments for a specific learning path
   */
  async getAssignmentsForLearningPath(
    learningPathId: string
  ): Promise<LearningPathAssignment[]> {
    const response = await this.listAssignments({
      learning_path_id: learningPathId,
      limit: 100,
    });

    return response.result;
  }

  /**
   * Get assignment completion statistics
   */
  async getAssignmentCompletionStats(assignmentId: string) {
    try {
      const assignment = await this.getAssignmentById(assignmentId);
      const stats = await this.updateAssignmentStats(assignmentId);

      return {
        assignment,
        stats,
        completionRate: stats.completion_rate,
        totalAssignees: assignment.total_assignees,
        completedCount: assignment.completed_count,
        inProgressCount: assignment.in_progress_count,
        notStartedCount: assignment.not_started_count,
      };
    } catch (error) {
      console.error('Failed to fetch assignment completion stats:', error);
      throw error;
    }
  }

  /**
   * Revoke a GROUP or ROLE learning path assignment
   * DELETE /api/learning-path-assignments/revoke/assignment/{id}
   */
  async revokeAssignment(assignmentId: string): Promise<{
    assignment_deleted: number;
    lp_trackings_deleted: number;
    course_trackings_deleted: number;
    assessment_attempts_deleted: number;
    course_certificates_deleted: number;
    lp_certificates_deleted: number;
  }> {
    const response = await apiClient.delete(
      API_ENDPOINTS.LEARNING_PATH_ASSIGNMENTS.REVOKE_ASSIGNMENT(assignmentId)
    );
    return response.data?.data || response.data;
  }
}

export const learningPathAssignmentService = new LearningPathAssignmentService();
